"use client";

import { useRouter } from "next/navigation";
import type * as React from "react";
import { useEffect } from "react";
import { buildLoginUrl, getCurrentReturnToPath, useAuth } from "@/lib/auth";

type AuthGateProps = {
  children: React.ReactNode;
  requireAdmin?: boolean;
};

export const AuthGate = ({ children, requireAdmin = false }: AuthGateProps) => {
  const router = useRouter();
  const { user, loading, status, isAdmin } = useAuth();

  useEffect(() => {
    if (loading || user || status !== "unauthenticated") return;
    router.replace(buildLoginUrl(getCurrentReturnToPath()));
  }, [loading, user, status, router]);

  useEffect(() => {
    if (loading || !user || !requireAdmin || isAdmin !== false) return;
    router.replace("/dashboard");
  }, [loading, user, requireAdmin, isAdmin, router]);

  // Only block on loading when no user has been resolved yet (initial load).
  // During background refetches loading is true but user is already populated —
  // gating on loading in that case causes a visible flash for authenticated users.
  //
  // When status === "error": non-admin pages are NOT blocked here so that
  // page-level error recovery UI (e.g. the billing page retry panel) can render.
  // Admin-gated pages are already blocked by (requireAdmin && isAdmin !== true)
  // because isAdmin is always null when user is null.
  if ((!user && loading) || status === "unauthenticated" || (requireAdmin && isAdmin !== true)) {
    return null;
  }

  return children;
};
